// contexts/SystemThemeContext.jsx
import { createContext, useContext, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { createTheme } from "@mui/material/styles";

// Import your existing theme structure
import theme from "assets/theme";
import colors from "assets/theme/base/colors";
import colorsDark from "assets/theme/base/ColorsDark";


const SystemThemeContext = createContext(null);

export const useSystemTheme = () => {
  const context = useContext(SystemThemeContext);
  if (!context) {
    throw new Error("useSystemTheme must be used within SystemThemeProvider");
  }
  return context;
};

export const SystemThemeProvider = ({ children }) => {
  const [mode, setMode] = useState("light");

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    
    const handleChange = (e) => {
      setMode(e.matches ? "dark" : "light");
    };
    
    setMode(mediaQuery.matches ? "dark" : "light");
    mediaQuery.addEventListener("change", handleChange);
    
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const dynamicTheme = useMemo(() => {
    const selectedColors = mode === "dark" ? colorsDark : colors;
    const modifiedTheme = {
      ...theme,
      palette: {
        ...theme.palette,
        ...selectedColors,
        mode: mode,
        background: {
          default: selectedColors.background.default,
          paper: selectedColors.background.paper || selectedColors.white.main,
        },
        text: {
          primary: mode === "dark" ? "#ffffff" : selectedColors.text.main,
          secondary: mode === "dark" ? "#a0aec0" : selectedColors.text.main,
        },
      },
    };
    
    return createTheme(modifiedTheme);
  }, [mode, theme]);

  return (
    <SystemThemeContext.Provider value={{ mode, theme: dynamicTheme }}>
      {children}
    </SystemThemeContext.Provider>
  );
};

SystemThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};